
import type { DuplicateDir } from "./types";

/* ═══════════════════════ FormatTools ═══════════════════════ */

/* ── OOXML pipeline (slideOoxml) ─────────────────────────────────── */

/** PresentationML / DrawingML / relationship XML namespaces. */
export const NS_P = "http://schemas.openxmlformats.org/presentationml/2006/main";
export const NS_A = "http://schemas.openxmlformats.org/drawingml/2006/main";
export const NS_R = "http://schemas.openxmlformats.org/officeDocument/2006/relationships";

/** English Metric Units per point (PowerPoint's internal unit). */
export const EMU_PER_POINT = 12700;

/** getFileAsync slice size (4 MB) and transient-error retry budget. */
export const SLICE_SIZE = 4 * 1024 * 1024;
export const MAX_RETRIES = 3;

/* ── Slide geometry (formatOps) ──────────────────────────────────── */

// PowerPoint JS API doesn't expose slide dimensions; default to standard
// widescreen 13.33" x 7.5" = 960 x 540 pts.
export const SLIDE_WIDTH = 960;
export const SLIDE_HEIGHT = 540;

/** Points increment for increase/decrease spacing. */
export const SPACING_STEP = 10;

/** Offset direction per duplicate action, in bounding-box units. */
export const DUPLICATE_DELTA: Record<DuplicateDir, [number, number]> = {
  left: [-1, 0],
  right: [1, 0],
  up: [0, -1],
  down: [0, 1],
};

/** A circle arrangement needs at least this many shapes to have a ring to
 * lay out — below it, radius/angle/arc math (which divides by `n` or `n-1`)
 * breaks down. Shared by `alignCircle`'s initial-selection check and
 * `removeCircleShape`'s floor on the Shapes stepper. */
export const MIN_CIRCLE_SHAPES = 2;
/** Upper bound for the Shapes stepper's "+" — guards against runaway native
 * shape creation (each one is its own OOXML round trip via duplicateSelection). */
export const MAX_CIRCLE_SHAPES = 60;

/* ── Fit-to-slide tuning (formatOps) ─────────────────────────────── */

export const FIT_SIDE_MARGIN_PCT = 0.04; // 4% left/right margins
export const FIT_FOOTER_BAND_PCT = 0.08; // 8% footer band
export const FIT_TITLE_GAP_PTS = 18; // gap between title and body
export const FIT_MIN_TOP_MARGIN = 16; // top margin when no title detected
export const FIT_DEFAULT_TITLE_PCT = 0.12; // fallback title band (no title found)
export const ASPECT_TOLERANCE = 0.02; // within 2% of 1:1 → treat as square/circle

/* ── Replace Font (fontData) ─────────────────────────────────────── */


export const COMMON_FONTS = [
  // Sans-serif / UI
  "Arial", "Arial Black", "Arial Narrow", "Arial Nova", "Arial Rounded MT Bold",
  "Bahnschrift", "Calibri", "Calibri Light", "Candara", "Candara Light", "Century Gothic",
  "Corbel", "Corbel Light", "Ebrima", "Franklin Gothic Book", "Franklin Gothic Demi",
  "Franklin Gothic Medium", "Gadugi", "Gill Sans MT", "Gill Sans MT Condensed",
  "Gill Sans Nova", "Leelawadee UI", "Lucida Sans", "Lucida Sans Unicode",
  "Microsoft Sans Serif", "News Gothic MT", "Segoe UI", "Segoe UI Light", "Segoe UI Semibold",
  "Segoe UI Semilight", "Segoe UI Black", "Tahoma", "Trebuchet MS", "Tw Cen MT", "Verdana",
  // Serif
  "Bell MT", "Bodoni MT", "Book Antiqua", "Bookman Old Style", "Californian FB", "Calisto MT",
  "Cambria", "Cambria Math", "Centaur", "Century", "Century Schoolbook", "Constantia",
  "Garamond", "Georgia", "Goudy Old Style", "High Tower Text", "Lucida Bright", "Lucida Fax",
  "Palatino Linotype", "Perpetua", "Rockwell", "Rockwell Condensed", "Rockwell Extra Bold",
  "Sitka Text", "Sylfaen", "Times New Roman",
  // Monospace
  "Cascadia Code", "Cascadia Mono", "Consolas", "Courier New", "Lucida Console",
  "Lucida Sans Typewriter", "OCR A Extended",
  // Display / decorative
  "Agency FB", "Algerian", "Bauhaus 93", "Berlin Sans FB", "Berlin Sans FB Demi",
  "Bernard MT Condensed", "Britannic Bold", "Broadway", "Cooper Black",
  "Copperplate Gothic Bold", "Copperplate Gothic Light", "Elephant", "Engravers MT",
  "Eras Bold ITC", "Eras Demi ITC", "Eras Light ITC", "Eras Medium ITC", "Felix Titling",
  "Footlight MT Light", "Forte", "Gill Sans Ultra Bold", "Gloucester MT Extra Condensed",
  "Goudy Stout", "Haettenschweiler", "Harrington", "Impact", "Imprint MT Shadow", "Jokerman",
  "Juice ITC", "Kristen ITC", "Maiandra GD", "Matura MT Script Capitals", "Modern No. 20",
  "Niagara Engraved", "Niagara Solid", "Onyx", "Perpetua Titling MT", "Playbill",
  "Poor Richard", "Ravie", "Showcard Gothic", "Snap ITC", "Stencil", "Tempus Sans ITC",
  "Wide Latin",
  // Script / handwriting
  "Blackadder ITC", "Bradley Hand ITC", "Brush Script MT", "Chiller", "Comic Sans MS",
  "Curlz MT", "Edwardian Script ITC", "Freestyle Script", "French Script MT", "Gigi",
  "Harlow Solid Italic", "Informal Roman", "Ink Free", "Kunstler Script", "Lucida Calligraphy",
  "Lucida Handwriting", "Magneto", "Mistral", "Monotype Corsiva", "Palace Script MT",
  "Papyrus", "Parchment", "Pristina", "Rage Italic", "Script MT Bold", "Segoe Print",
  "Segoe Script", "Viner Hand ITC", "Vivaldi", "Vladimir Script",
  // Symbol
  "Marlett", "MT Extra", "Symbol", "Webdings", "Wingdings", "Wingdings 2", "Wingdings 3",
  // Microsoft 365 cloud fonts
  "Aptos", "Aptos Display", "Aptos Mono", "Aptos Serif", "Bierstadt", "Grandview",
  "Georgia Pro", "Seaford", "Skeena", "Tenorite", "Verdana Pro",
  // CJK / international
  "Malgun Gothic", "Meiryo", "Microsoft JhengHei", "Microsoft YaHei", "MS Gothic", "MS Mincho",
  "MS PGothic", "SimSun", "SimHei", "NSimSun", "Yu Gothic", "Yu Mincho", "DengXian", "KaiTi",
  "FangSong", "Batang", "Dotum", "Gulim", "MingLiU", "PMingLiU", "Nirmala UI", "Mangal",
  "Gautami", "Latha",
];

/* ── Grid config dialog (UI) ─────────────────────────────────────── */

export const MIN_DIM = 1;
export const MAX_DIM = 20;
export const MIN_GUTTER = 0;
export const MAX_GUTTER = 5;
export const DIM_STEP = 1;
export const GUTTER_STEP = 0.05;
